/**
 * Main App Component
 * Routing and Auth Provider
 */
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CandidateRegistrationPage from './pages/CandidateRegistrationPage';
import OTPVerificationPage from './pages/OTPVerificationPage';
import CandidateDashboard from './pages/CandidateDashboard';
import HRDashboard from './pages/HRDashboard';
import ResignedUsersPage from './pages/ResignedUsersPage';
import CareerPage from './pages/CareerPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminJobManagementPage from './pages/AdminJobManagementPage';
import AdminCreateApplicationPage from './pages/AdminCreateApplicationPage';
import ChairmanDashboard from './pages/ChairmanDashboard';
import ChairmanHRPerformancePage from './pages/ChairmanHRPerformancePage';
import ChairmanSummaryReportPage from './pages/ChairmanSummaryReportPage';
import ChairmanReportsPage from './pages/ChairmanReportsPage';
import ChairmanCorrectionsPage from './pages/ChairmanCorrectionsPage';
import ChairmanFinancePage from './pages/ChairmanFinancePage';
import ChairmanAssessmentPage from './pages/ChairmanAssessmentPage';
import CandidateProfilePage from './pages/CandidateProfilePage';
import ProfilePage from './pages/ProfilePage';
import CandidatesListPage from './pages/CandidatesListPage';
import EnhancedApplicationFormPage from './pages/EnhancedApplicationFormPage';
import MyApplicationsPage from './pages/MyApplicationsPage';
import ApplicationDetailsPage from './pages/ApplicationDetailsPage';
import ApplicationsListPage from './pages/ApplicationsListPage';
import CandidateDocumentsPage from './pages/CandidateDocumentsPage';
import AdminDocumentsPage from './pages/AdminDocumentsPage';
import InterviewCalendarPage from './pages/InterviewCalendarPage';
import MyAttendancePage from './pages/MyAttendancePage';
import HRAttendanceDashboard from './pages/HRAttendanceDashboard';
import CreateUserPage from './pages/CreateUserPage';
import UsersManagementPage from './pages/UsersManagementPage';
import AdminSecurityPage from './pages/AdminSecurityPage';
import CandidateJobApplicationsPage from './pages/CandidateJobApplicationsPage';
import JobApplicationFormPage from './pages/JobApplicationFormPage';
import LeaveManagementPage from './pages/LeaveManagementPage';
import LeaveRequestsPage from './pages/LeaveRequestsPage';
import LeavePolicyManagementPage from './pages/LeavePolicyManagementPage';
import MyLeaveBalancesPage from './pages/MyLeaveBalancesPage';
import LeaveBalanceAssignmentPage from './pages/LeaveBalanceAssignmentPage';
import ShiftManagementPage from './pages/ShiftManagementPage';
import ShiftAssignmentPage from './pages/ShiftAssignmentPage';
import AttendanceCorrectionPage from './pages/AttendanceCorrectionPage';
import DutyManagementPage from './pages/DutyManagementPage';
import ResignationApprovalPage from './pages/ResignationApprovalPage';
import ReportsHomePage from './pages/reports/ReportsHomePage';
import SummaryReportPage from './pages/reports/SummaryReportPage';
import HRPerformanceReportPage from './pages/reports/HRPerformanceReportPage';
import InterviewReportPage from './pages/reports/InterviewReportPage';
import ExamReportPage from './pages/reports/ExamReportPage';
import PaymentReportPage from './pages/reports/PaymentReportPage';
import DepartureReportPage from './pages/reports/DepartureReportPage';
import PaymentsPage from './pages/PaymentsPage';
import OfficeTransactionPage from './pages/OfficeTransactionPage';
import ChairmanOfficeTransactionPage from './pages/ChairmanOfficeTransactionPage';
import ChairmanLogsPage from './pages/ChairmanLogsPage';
import HRPageVisitTracker from './components/HRPageVisitTracker';
import HRDailyReportPage from './pages/HRDailyReportPage';
import ChairmanDailyReportsPage from './pages/ChairmanDailyReportsPage';
import AdminTutorialVideosPage from './pages/AdminTutorialVideosPage';
import AdminIDCardPage from './pages/AdminIDCardPage';
import ContactsPage from './pages/ContactsPage';
import CandidateGrievancePage from './pages/CandidateGrievancePage';
import AdminGrievancesPage from './pages/AdminGrievancesPage';

// Home redirect component - redirects to role-specific dashboard or landing
const HomeRedirect = () => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <LandingPage />;
  }

  // Redirect to role-specific dashboard
  switch (user?.role) {
    case 'CANDIDATE':
      return <Navigate to="/candidate/dashboard" replace />;
    case 'HR':
      return <Navigate to="/hr/dashboard" replace />;
    case 'HRO':
      return <Navigate to="/hro/dashboard" replace />;
    case 'ADMIN':
      return <Navigate to="/admin/dashboard" replace />;
    case 'CHAIRMAN':
      return <Navigate to="/chairman/dashboard" replace />;
    default:
      return <Navigate to="/landing" replace />;
  }
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <HRPageVisitTracker />
        <div className="min-h-screen bg-background">
          <Routes>
            {/* Public routes */}
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/career" element={<CareerPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/register" element={<CandidateRegistrationPage />} />
            <Route path="/verify-otp" element={<OTPVerificationPage />} />

            {/* Protected routes - Role-specific dashboards */}
            <Route
              path="/candidate/dashboard"
              element={
                <ProtectedRoute allowedRoles={['CANDIDATE']}>
                  <CandidateDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/candidate/profile"
              element={
                <ProtectedRoute allowedRoles={['CANDIDATE']}>
                  <CandidateProfilePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/candidate/applications"
              element={
                <ProtectedRoute allowedRoles={['CANDIDATE']}>
                  <MyApplicationsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/candidate/applications/new"
              element={
                <ProtectedRoute allowedRoles={['CANDIDATE']}>
                  <EnhancedApplicationFormPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/candidate/documents"
              element={
                <ProtectedRoute allowedRoles={['CANDIDATE']}>
                  <CandidateDocumentsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/candidate/grievances"
              element={
                <ProtectedRoute allowedRoles={['CANDIDATE']}>
                  <CandidateGrievancePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/grievances"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'CHAIRMAN', 'HR', 'HRO']}>
                  <AdminGrievancesPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hr/dashboard"
              element={
                <ProtectedRoute allowedRoles={['HR']}>
                  <HRDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hro/dashboard"
              element={
                <ProtectedRoute allowedRoles={['HRO']}>
                  <HRDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/resigned-users"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'HRO']}>
                  <ResignedUsersPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hro/resigned-users"
              element={
                <ProtectedRoute allowedRoles={['HRO']}>
                  <ResignedUsersPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hr/profile"
              element={
                <ProtectedRoute allowedRoles={['HR']}>
                  <CandidateProfilePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hr/applications"
              element={
                <ProtectedRoute allowedRoles={['HR', 'ADMIN', 'CHAIRMAN']}>
                  <ApplicationsListPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hr/interviews"
              element={
                <ProtectedRoute allowedRoles={['HR', 'ADMIN', 'CHAIRMAN']}>
                  <InterviewCalendarPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hr/attendance"
              element={
                <ProtectedRoute allowedRoles={['HR']}>
                  <MyAttendancePage />
                </ProtectedRoute>
              } 
            />
            
            <Route
              path="/hr/daily-report"
              element={
                <ProtectedRoute allowedRoles={['HR']}>
                  <HRDailyReportPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hr/leave"
              element={
                <ProtectedRoute allowedRoles={['HR']}>
                  <LeaveManagementPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hro/profile"
              element={
                <ProtectedRoute allowedRoles={['HRO']}>
                  <CandidateProfilePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hro/attendance"
              element={
                <ProtectedRoute allowedRoles={['HRO']}>
                  <HRAttendanceDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hro/leave-requests"
              element={
                <ProtectedRoute allowedRoles={['HRO', 'ADMIN']}>
                  <LeaveRequestsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hro/leave-policy"
              element={
                <ProtectedRoute allowedRoles={['HRO', 'ADMIN']}>
                  <LeavePolicyManagementPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hro/leave-balance-assignment"
              element={
                <ProtectedRoute allowedRoles={['HRO', 'ADMIN']}>
                  <LeaveBalanceAssignmentPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hr/my-leave-balances"
              element={
                <ProtectedRoute allowedRoles={['HR']}>
                  <MyLeaveBalancesPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hro/duty-management"
              element={
                <ProtectedRoute allowedRoles={['HRO']}>
                  <DutyManagementPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hro/shift-management"
              element={
                <ProtectedRoute allowedRoles={['HRO']}>
                  <ShiftManagementPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hro/shift-assignment"
              element={
                <ProtectedRoute allowedRoles={['HRO']}>
                  <ShiftAssignmentPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hro/attendance-correction"
              element={
                <ProtectedRoute allowedRoles={['HRO', 'CHAIRMAN']}>
                  <AttendanceCorrectionPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/applications/:id"
              element={
                <ProtectedRoute allowedRoles={['CANDIDATE', 'HR', 'ADMIN', 'CHAIRMAN']}>
                  <ApplicationDetailsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/profile"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <CandidateProfilePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/candidates"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'HR', 'CHAIRMAN']}>
                  <CandidatesListPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/documents"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'HR', 'CHAIRMAN']}>
                  <AdminDocumentsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/job-management"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <AdminJobManagementPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/create-application"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <AdminCreateApplicationPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/create-user"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <CreateUserPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/users"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <UsersManagementPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/id-card"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'CHAIRMAN']}>
                  <AdminIDCardPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/security"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'CHAIRMAN']}>
                  <AdminSecurityPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/resignations"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <ResignationApprovalPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/candidate/job-applications"
              element={
                <ProtectedRoute allowedRoles={['CANDIDATE']}>
                  <CandidateJobApplicationsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/candidate/job-application-form/:jobRoleId"
              element={
                <ProtectedRoute allowedRoles={['CANDIDATE']}>
                  <JobApplicationFormPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chairman/dashboard"
              element={
                <ProtectedRoute allowedRoles={['CHAIRMAN']}>
                  <ChairmanDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chairman/reports"
              element={
                <ProtectedRoute allowedRoles={['CHAIRMAN']}>
                  <ChairmanReportsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chairman/assessment"
              element={
                <ProtectedRoute allowedRoles={['CHAIRMAN']}>
                  <ChairmanAssessmentPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chairman/finance"
              element={
                <ProtectedRoute allowedRoles={['CHAIRMAN']}>
                  <ChairmanFinancePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chairman/reports/hr-performance"
              element={
                <ProtectedRoute allowedRoles={['CHAIRMAN']}>
                  <ChairmanHRPerformancePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chairman/reports/summary"
              element={
                <ProtectedRoute allowedRoles={['CHAIRMAN']}>
                  <ChairmanSummaryReportPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chairman/reports/departure"
              element={
                <ProtectedRoute allowedRoles={['CHAIRMAN']}>
                  <DepartureReportPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chairman/profile"
              element={
                <ProtectedRoute allowedRoles={['CHAIRMAN']}>
                  <CandidateProfilePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chairman/corrections"
              element={
                <ProtectedRoute allowedRoles={['CHAIRMAN']}>
                  <ChairmanCorrectionsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chairman/attendance-correction"
              element={
                <ProtectedRoute allowedRoles={['CHAIRMAN']}>
                  <AttendanceCorrectionPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chairman/daily-reports"
              element={
                <ProtectedRoute allowedRoles={['CHAIRMAN']}>
                  <ChairmanDailyReportsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chairman/logs"
              element={
                <ProtectedRoute allowedRoles={['CHAIRMAN']}>
                  <ChairmanLogsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'HRO', 'HR', 'CHAIRMAN']}>
                  <ReportsHomePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports/summary"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'HRO', 'HR', 'CHAIRMAN']}>
                  <SummaryReportPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports/hr-performance"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'HRO', 'HR', 'CHAIRMAN']}>
                  <HRPerformanceReportPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports/interviews"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'HRO', 'HR', 'CHAIRMAN']}>
                  <InterviewReportPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports/exams"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'HRO', 'HR', 'CHAIRMAN']}>
                  <ExamReportPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports/payments"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'HRO', 'HR', 'CHAIRMAN']}>
                  <PaymentReportPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/payments"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'HR', 'HRO', 'CHAIRMAN']}>
                  <PaymentsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/payments"
              element={<Navigate to="/payments" replace />}
            />

            {/* Office Transaction routes */}
            <Route
              path="/admin/office-transactions"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <OfficeTransactionPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/tutorial-videos"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'CHAIRMAN']}>
                  <AdminTutorialVideosPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/contacts"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'CHAIRMAN']}>
                  <ContactsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chairman/office-transactions"
              element={
                <ProtectedRoute allowedRoles={['CHAIRMAN']}>
                  <ChairmanOfficeTransactionPage />
                </ProtectedRoute>
              }
            />

            {/* Profile route - accessible to all authenticated users */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute allowedRoles={['CANDIDATE', 'HR', 'HRO', 'ADMIN', 'CHAIRMAN']}>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />

            {/* Default redirect */}
            <Route path="/" element={<HomeRedirect />} />

            {/* 404 - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
